import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiTrash2, FiMove, FiSearch, FiSave, FiLayers } from 'react-icons/fi';
import { dummyExercises } from '../data/dummyData';

export default function RoutineBuilder() {
  const [searchTerm, setSearchTerm] = useState('');
  const [routine, setRoutine] = useState([]);

  const filteredExercises = dummyExercises.filter(ex => 
    ex.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    ex.muscle.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToRoutine = (exercise) => {
    setRoutine([...routine, { ...exercise, uniqueId: Math.random().toString(), sets: 3, reps: 10 }]);
  };

  const removeFromRoutine = (uniqueId) => {
    setRoutine(routine.filter(ex => ex.uniqueId !== uniqueId));
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-6">
      {/* Left: Library Pool */}
      <div className="w-full lg:w-1/3 glass-panel p-4 flex flex-col h-full">
        <h3 className="text-gray-400 text-sm font-mono mb-4">DATA.BANK_POOL</h3>
        <div className="relative mb-4">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input 
            type="text" 
            placeholder="Search parameters..." 
            className="w-full bg-carbon-900 border border-carbon-600 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-lime-500 text-white transition-colors"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto pr-2 space-y-2">
          {filteredExercises.map(ex => (
            <motion.div 
              whileHover={{ scale: 1.02 }}
              key={ex.id} 
              className="bg-carbon-800 border border-carbon-600 p-3 rounded-lg flex justify-between items-center group cursor-pointer"
              onClick={() => addToRoutine(ex)}
            >
              <div>
                <div className="text-sm font-semibold text-white group-hover:text-lime-500 transition-colors">{ex.name}</div>
                <div className="text-xs text-gray-500 font-mono mt-1">{ex.muscle} • {ex.category}</div>
              </div>
              <button className="bg-carbon-700 p-2 rounded text-gray-400 group-hover:text-lime-500 group-hover:bg-carbon-600 transition-all">
                <SafeIcon icon={FiPlus} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Right: Active Routine */}
      <div className="w-full lg:w-2/3 glass-panel p-4 flex flex-col h-full relative overflow-hidden">
        {/* Decorative background grid */}
        <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
        
        <div className="relative z-10 flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight">New Sequence_Alpha</h2>
            <p className="text-sm text-gray-400 font-mono">Estimated Load Time: ~65 mins</p>
          </div>
          <button className="flex items-center gap-2 bg-white text-carbon-900 px-4 py-2 rounded font-bold text-sm tracking-wider hover:bg-gray-200 transition-colors">
            <SafeIcon icon={FiSave} /> COMMIT
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 relative z-10 p-1">
          <AnimatePresence>
            {routine.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-carbon-600 rounded-xl"
              >
                <SafeIcon icon={FiLayers} className="text-4xl mb-4 opacity-50" />
                <p className="font-mono text-sm">AWAITING_INPUT_DATA</p>
                <p className="text-xs mt-2">Select parameters from the data bank</p>
              </motion.div>
            ) : (
              routine.map((ex, index) => (
                <motion.div
                  key={ex.uniqueId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  layout
                  className="bg-carbon-900 border border-carbon-600 rounded-lg p-4 flex items-center gap-4 group hover:border-lime-500/50 transition-colors"
                >
                  <div className="cursor-grab text-gray-600 hover:text-white"><SafeIcon icon={FiMove} /></div>
                  <div className="text-lime-500 font-mono text-xl font-bold w-8">{String(index + 1).padStart(2, '0')}</div>
                  <div className="flex-1">
                    <div className="text-white font-semibold">{ex.name}</div>
                    <div className="text-xs text-gray-500">{ex.category}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-gray-500 font-mono uppercase">Sets</span>
                      <input type="number" defaultValue={ex.sets} className="w-12 bg-carbon-800 border-b border-carbon-600 text-center text-white focus:outline-none focus:border-lime-500 py-1" />
                    </div>
                    <span className="text-gray-600">×</span>
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] text-gray-500 font-mono uppercase">Reps</span>
                      <input type="number" defaultValue={ex.reps} className="w-12 bg-carbon-800 border-b border-carbon-600 text-center text-white focus:outline-none focus:border-lime-500 py-1" />
                    </div>
                  </div>
                  <button onClick={() => removeFromRoutine(ex.uniqueId)} className="text-gray-600 hover:text-red-500 p-2 transition-colors">
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}